import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

import { normalize, sameText } from "../../sentenceDiff";

import ClozeSentence from "../ClozeSentence";
import Icon from "../Icon";
import Modal from "../Modal";
import ModalFooterCloseBtn from "../ModalFooterCloseBtn";
import VoiceRecorder from "./VoiceRecorder";

const WRITING_FEEDBACK_EMOJI = {
  complete: "🎉",
  offTrack: "😬",
  onTrack: "👍"
};

// Case and whitespace don't count against copying the sentence out, and neither does
// leaving off the final punctuation - the same leniency the game itself grades with.
const fold = (text) => text.toLowerCase();

// "complete" once the whole sentence is typed, "onTrack" while what's typed so far is
// the start of it, "offTrack" otherwise, null before anything is typed.
export function writingFeedback(typed, sentenceText) {
  if(!normalize(typed)) {
    return null;
  }

  if(sameText(typed, sentenceText, { normalizeToken: fold })) {
    return "complete";
  }

  return fold(normalize(sentenceText)).startsWith(fold(normalize(typed))) ? "onTrack" : "offTrack";
}

// Keys for the Speak it dialog. The play page's own hotkeys stand down while a modal is
// open, so these take over; ctrl+Enter / ctrl+Space work as they do in the game.
export function speakingHotkey(e) {
  if(e.metaKey || e.altKey) {
    return null;
  }

  const key = (e.key || "").toLowerCase();
  if(e.ctrlKey) {
    if(key === "enter") {
      return "record";
    }
    if(key === " ") {
      return e.shiftKey ? "slowAudio" : "audio";
    }
    return null;
  }

  if(key === "r") {
    return "record";
  }
  if(key === "p") {
    return "playRecording";
  }
  if(key === "a") {
    return e.shiftKey ? "slowAudio" : "audio";
  }
  if(key === "enter") {
    return "close";
  }
  return null;
}

const SPEAKING_HOTKEYS = [
  ["R", "record / stop"],
  ["P", "play your recording"],
  ["A", "sentence audio"],
  ["shift+A", "half speed"],
  ["Enter", "close"]
];

function MorePracticeControls({ hotkeysEnabled = true, playSentenceAudio, sentence, writeItAvailable = true }, ref) {
  const [speakingVisible, setSpeakingVisible] = useState(false);
  const [writingText, setWritingText] = useState("");
  const [writing, setWriting] = useState(false);
  const speakingModal = useRef(null);
  const voiceRecorder = useRef(null);
  // the listener is added once per opening, so it reads the latest audio callback from here
  const playSentenceAudioRef = useRef(playSentenceAudio);
  playSentenceAudioRef.current = playSentenceAudio;

  useEffect(() => {
    if(!speakingVisible || !hotkeysEnabled) {
      return undefined;
    }

    const onKeyDown = (e) => {
      const action = speakingHotkey(e);
      if(!action || (e.repeat && action === "record")) {
        return;
      }

      // also keeps Enter / space from clicking whichever dialog button has focus
      e.preventDefault();
      if(action === "record") {
        voiceRecorder.current?.toggleRecording();
      }
      else if(action === "playRecording") {
        voiceRecorder.current?.playRecording();
      }
      else if(action === "audio") {
        playSentenceAudioRef.current();
      }
      else if(action === "slowAudio") {
        playSentenceAudioRef.current({ speed: 0.5 });
      }
      else if(action === "close") {
        speakingModal.current?.hide();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [speakingVisible, hotkeysEnabled]);

  // for the play page's alt+w / alt+v hotkeys
  useImperativeHandle(ref, () => ({
    showSpeaking: () => setSpeakingVisible(true),
    toggleWriting: () => writeItAvailable && setWriting((w) => !w)
  }), [writeItAvailable]);

  const plainText = sentence.text.replace(/{{|}}/g, "");
  const feedback = writingFeedback(writingText, plainText);

  return (
    <div className="more-practice" style={{ marginBottom: 10 }}>
      <div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
        <span>More practice:</span>
        {writeItAvailable && (
          <button className={`btn btn-default btn-sm write-it ${writing ? "active" : ""}`} onClick={() => setWriting(!writing)} type="button">Write it</button>
        )}
        <button className="btn btn-default btn-sm speak-it" onClick={() => setSpeakingVisible(true)} type="button">Speak it</button>
      </div>
      {writing && (
        // capped to roughly the sentence's width rather than the whole play area, with the emoji
        // beside the input (its space reserved) so nothing shifts as the feedback changes
        <div style={{ alignItems: "center", display: "flex", gap: 8, margin: "10px auto 0", maxWidth: 560 }}>
          <input
            aria-label="Write the sentence"
            autoFocus={true}
            className="form-control write-it-input"
            onChange={(e) => setWritingText(e.target.value)}
            // keep the play hotkeys (Enter for Next, space for audio, ...) out of the way while typing
            onKeyDown={(e) => e.stopPropagation()}
            style={{ flex: 1, textAlign: "center" }}
            value={writingText}
          />
          <div className="write-it-feedback" data-feedback={feedback || ""} style={{ fontSize: "1.6em", opacity: feedback ? 1 : 0, width: "1.4em" }}>
            {WRITING_FEEDBACK_EMOJI[feedback] || "\u00A0"}
          </div>
        </div>
      )}
      {speakingVisible && (
        <Modal
          footer={<ModalFooterCloseBtn />}
          onHidden={() => setSpeakingVisible(false)}
          ref={speakingModal}
          show={speakingVisible}
          title="Speak it"
        >
          <p>
            Record your voice and compare it to the sentence audio! Recording happens in your browser.
            The recording is not sent to or saved by Clozemaster.
          </p>
          <hr />
          <div style={{ fontSize: "2em" }}><ClozeSentence text={sentence.text} /></div>
          <p>{sentence.translation}</p>
          <div className="btn-group">
            <button className="btn btn-default" onClick={() => playSentenceAudio()} title="Play sentence audio" type="button">
              <Icon name="volume-up" />
            </button>
            <button
              className="btn btn-default"
              onClick={() => playSentenceAudio({ speed: 0.5 })}
              style={{ margin: "0 0 0 -1px" }}
              title="Play sentence audio half speed"
              type="button"
            >
              &frac12;
            </button>
          </div>
          <hr />
          <VoiceRecorder ref={voiceRecorder} />
          {hotkeysEnabled && (
            <p className="speak-it-hotkeys text-secondary" style={{ marginTop: 10 }}>
              <small>
                {SPEAKING_HOTKEYS.map(([key, label], i) => (
                  <React.Fragment key={key}>
                    {i > 0 && " · "}
                    <kbd>{key}</kbd> {label}
                  </React.Fragment>
                ))}
              </small>
            </p>
          )}
        </Modal>
      )}
    </div>
  );
}

export default forwardRef(MorePracticeControls);
